import { ActionIcon, Box, Button, Divider, Flex } from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useDynamicUI } from '../DynamicUiContext';
import { FieldWithValue, UiEntityList } from '../types';
import DynamicLayoutItem from './DynamicLayoutItem';
import { DynamicListEntityItemDeletionModal } from 'src/UI/dynamicUI/layout/DynamicEntitySection';
import React, { useState } from 'react';
import { useDisclosure } from '@mantine/hooks';

interface DynamicEntityListProps {
  entityList: UiEntityList;
}

export default function DynamicEntityList({ entityList }: DynamicEntityListProps) {
  const { isEditing, form, uiData, setIsEditing } = useDynamicUI();
  const [opened, { open, close }] = useDisclosure(false);
  const [deletionIndex, setDeletionIndex] = useState<number | null>(null);

  const formListValues = form.getValues()[entityList.entityType];
  const isArray = Array.isArray(formListValues);
  if (!isArray || !uiData) {
    return null;
  }
  const defaultFields = entityList.children.reduce((defaultFields, uiField) => {
    if (uiField.type === 'FIELD_ROW') {
      const innerFields = uiField.children.reduce((defaultInnerFields, uiInnerField) => {
        return { ...defaultInnerFields, [uiInnerField.entityField]: '' };
      }, {} as FieldWithValue);
      return { ...defaultFields, ...innerFields };
    }
    return { ...defaultFields, [uiField.entityField]: '' };
  }, {} as FieldWithValue);

  const handleDeletePress = (index: number) => {
    setDeletionIndex(index);
    open();
  };

  return (
    <Box
      key={`${entityList.type}${entityList.entityType}${entityList.children
        .map(c => c.type)
        .join()}`}
    >
      {deletionIndex !== null && (
        <DynamicListEntityItemDeletionModal
          form={form}
          entityType={entityList.entityType}
          index={deletionIndex}
          opened={opened}
          close={close}
        />
      )}
      {formListValues.map((entity, entityIndex) => (
        <Box key={entityIndex} data-testid={`entity-list-item-${entityIndex}`}>
          <Flex mb={'md'} align={'start'} direction={'row'}>
            <Box>
              {entityList.children.map((child, i) => (
                <DynamicLayoutItem key={`${entityIndex}-${i}`} item={child} index={entityIndex} />
              ))}
            </Box>
            {isEditing && (
              <ActionIcon
                aria-label="Remove"
                color="gray.2"
                ml={'md'}
                mt={'xl'}
                w={32}
                h={32}
                onClick={() => {
                  handleDeletePress(entityIndex);
                }}
              >
                <IconTrash
                  color="var(--mantine-color-gray-7)"
                  stroke={1.5}
                  size={20}
                  data-testid="delete-button"
                />
              </ActionIcon>
            )}
          </Flex>
          {entityIndex !== formListValues.length - 1 && <Divider my="xxl" />}
        </Box>
      ))}
      <Box>
        <Button
          variant="pill"
          leftSection={<IconPlus size={16} />}
          data-testid="add-button"
          onClick={() => {
            form.insertListItem(entityList.entityType, defaultFields);
            setIsEditing(true);
          }}
        >
          {entityList.addButtonLabel}
        </Button>
      </Box>
    </Box>
  );
}
